import * as React from 'react';
import FormRow from '../../../../../../components/FormRow';
import InputHandler from '../../../../../../components/InputHandler';
import { IFormContext, withFormContext } from '../../../../../../containers/FormContextHandler';
import { nowPartialDate } from 'neuro-utils';
import { formatSeizureClassification } from 'Routes/Seizure/utils';
import { seizureType } from 'neuro-schemas';
import { useIntl } from 'react-intl';

const allClassifications = seizureType.utils.classificationOptions as TSeizureClassification[];

/**
 * Indents options that are subclasses of another number, e.g., 1.0.1 is indented under 1.0.
 * @param {{ classification: string } | string} o - The option to be formatted (either a classification object or string)
 * @param {string} group - The group to display next to the option
 * @returns {React.ReactNode} The formatted option with appropriate indentation
 */
const formatOptionIndent = (o: string): React.ReactNode => {
  const classificationString = o;

  const partialIndentRegex = /^[0-9]\.([0-9])*\.*([0-9])*\.*/;

  const matches = classificationString.match(partialIndentRegex);
  return (
    <span style={{ marginLeft: ((matches || []).filter(m => m).length - 1) * 1.5 + 'rem' }}>
      {classificationString}
    </span>
  );
};

const _SeizureTypeForm = (props: IFormContext<ISeizureType>): React.JSX.Element => {
  const { editing, formData } = props;
  const prefix = 'diagnosis.epilepsy.seizureType.'; // for localization
  const options = allClassifications.map(sc => {
    const pieces = sc.split('.');
    // Set parent (top) group as group, instead of close group
    let group = pieces.length > 2 ? [pieces[0], pieces[1]].join('.') + '.' : pieces[0];

    const findChildClassification = allClassifications.filter(clas => clas.match('^' + sc));
    if (findChildClassification.length > 1) {
      // If this (top group) classification has child classifications, use this clas as group
      if (sc.split('.').length === 2) group = sc;
    }

    return {
      classification: sc,
      group: group,
    };
  });

  // Set visibleToPatient default value on mount
  React.useEffect(() => {
    if (formData.document?.visibleToPatient === undefined) {
      formData.onChange?.({ visibleToPatient: true });
    }
  }, []);

  const { formatMessage } = useIntl();
  const fm = (id: string, values?: Record<string, (chunks: string[]) => React.JSX.Element>) =>
    formatMessage({ id }, values);

  const onChangeClassification = (values: TOnChangeValues) => {
    const newClassfication = values['classification'] as { classification: string; group: string } | undefined;
    // Save classification without group field (which is included by AutoCompleteSelect)
    formData.onChange?.({ classification: { classification: newClassfication?.['classification'] } });
  };

  return (
    <React.Fragment>
      <FormRow title={prefix + 'firstOccurenceDate'}>
        <InputHandler
          name="firstOccurenceDate"
          type="PartialDate"
          editing={!!editing}
          formData={formData}
          dateHook={{
            dateHookCeiling: nowPartialDate(),
          }}
        />
      </FormRow>
      <FormRow title={prefix + 'seizureClassification'}>
        <div style={{ margin: '0 0 2rem 0', fontWeight: 600 }}>
          {formatSeizureClassification(
            formData.document.classification?.classification as TSeizureClassification,
            'long',
            true,
          )}
        </div>
        <InputHandler
          type="AutoCompleteSelect"
          name="classification"
          editing={!!editing}
          formData={{
            onChange: onChangeClassification,
            document: {
              classification: formData?.document.classification
                ? { classification: formData?.document.classification?.classification }
                : null,
            },
          }}
          options={options}
          placeholder={prefix + 'seizureClassificationPlaceholder'}
          width={50}
          getOptionLabel={(o: { classification: NonNullable<TSeizureClassification | undefined>; group: string }) => {
            // Regex to remove <strong> and </strong> tags from text input field
            const strongRegex = /<\/*strong>/g;
            return o.classification
              ? `${o.classification} ${props.fm(`seizure.opts.${o.classification}`).replace(strongRegex, '')}`
              : '';
          }}
          groupID="group"
          renderOption={(o: { classification: NonNullable<TSeizureClassification | undefined>; group: string }) =>
            o && (
              <div style={{ display: 'flex' }}>
                <span style={{ display: 'inline' }}>{formatOptionIndent(o.classification)}&nbsp;</span>

                <span style={{ display: 'inline' }}>
                  {fm(`seizure.opts.${o.classification}`, {
                    strong: (chunks: string[]) => (
                      <span key={chunks.join('')} style={{ fontWeight: '600', display: 'inline' }}>
                        {React.Children.toArray(chunks)}
                      </span>
                    ),
                  })}
                </span>
              </div>
            )
          }
          groupBy={(option: Record<string, string>) => {
            const strongRegex = /<\/*strong>/g;
            if (option['group'].split('.').length === 1) return '';
            return `${option['group']} ${props.fm(`seizure.opts.${option['group']}`).replace(strongRegex, '')}`;
          }}
          renderGroup={params => (
            <li key={params.key}>
              <ul style={{ margin: 0, padding: '0 0 0 1rem' }}>{params.children}</ul>
            </li>
          )}
        />
      </FormRow>
      <FormRow title={prefix + 'visibleToPatient'}>
        <InputHandler
          name="visibleToPatient"
          type="CheckboxSingle"
          editing={!!editing}
          formData={formData}
          option={'true'}
          showLabel={false}
        />
      </FormRow>
      <FormRow title={prefix + 'seizureNameByPatient'}>
        <InputHandler
          name="nameByPatient"
          type="TextField"
          editing={!!editing}
          formData={formData}
          placeholder={prefix + 'seizureNameByPatientPlaceholder'}
          width={50}
        />
      </FormRow>
      <FormRow title={prefix + 'seizureFreeDescription'}>
        <InputHandler
          type="TextArea"
          name="freeDescription"
          editing={true}
          formData={formData}
          placeholder={prefix + 'seizureFreeDescriptionPlaceholder'}
          width={50}
        />
      </FormRow>
    </React.Fragment>
  );
};

/** Component rendering epilepsy platform's seizure type docs' form. */
const SeizureTypeForm = withFormContext(_SeizureTypeForm);
export default SeizureTypeForm;
